/**
 * Set up a state (piece 25; decision 1780): the six steps that take a state
 * from no registration to one the returns can be cut from, each read from the
 * registration's own rows and the tables beside it. 🔒 Nothing here is stored;
 * the tax controller answers the facts and this function reads them.
 */
import type { VerificationStatus } from '../composites/addressVerification';
import type { LocalTaxCoverageValue } from './localTax';
import type { FilingFrequencyValue } from './rateTable';
import type { UnmappedTaxPairingRow } from './register';

export const STATE_SETUP_STEPS = ['register', 'frequency', 'rate', 'taxes', 'local', 'nexus'] as const;
export type StateSetupStep = (typeof STATE_SETUP_STEPS)[number];

/** The facts read for one state; a browser fetch path, relative to the API root. */
export const stateSetupPath = (stateCode: string): string => `/tax/states/${encodeURIComponent(stateCode)}/setup`;

/** One registration's own route, which the frequency step writes through (decision 1780). */
export const taxRegistrationPath = (id: string): string => `/tax/registrations/${encodeURIComponent(id)}`;

/** The registration in force today, as the first two steps read it. */
export interface StateSetupRegistration {
  id: string;
  registeredAt: string;
  accountNumber: string | null;
  filingFrequency: FilingFrequencyValue | null;
  dueDayOfMonth: number | null;
  dueMonthOffset: number | null;
  /** Whether a return has been cut for it; a frequency already set is changed only before one is (decision 1780). */
  hasReturns: boolean;
}

/** 🔒 The facts behind the six steps, each read from a row and none of them stored (decision 1780). */
export interface StateSetupView {
  stateCode: string;
  stateName: string;
  /** One of the five states with no statewide sales tax, which takes no registration and no stepper. */
  noStatewideTax: boolean;
  registration: StateSetupRegistration | null;
  /** The state's `SalesTaxRate` row in force today, and whether it is still the seed's starting point. */
  rate: { inForce: boolean; seeded: boolean; label: string | null };
  /** The active categories, and how many read Default or carry no answer, which is the review report's needs-review kind. */
  taxes: { categories: number; unsettled: number };
  /**
   * The coverage on the row in force, and the review report's two location kinds for the state. 🔒 The untaxed
   * invoices (decision 1830) are named on the local step's line and decide no step (decision 1831).
   */
  local: { coverage: LocalTaxCoverageValue | null; noCounty: number; unconfirmed: number; estimated?: number; untaxedInvoices?: number };
  /** Whether a `NexusThreshold` row stands today, and the crossing nobody has accepted yet. */
  nexus: { thresholdSet: boolean; crossedOn: string | null; acknowledgedAt: string | null };
}

export interface StateSetupStepFacts {
  done: boolean;
  /** Whether what the step needs is done; a step that cannot be reached draws as later. */
  reachable: boolean;
}

/**
 * 🔒 Each step's done test (decision 1780). Register is done once a
 * registration stands with its account number; the frequency once the
 * registration names one; the rate once the row in force is not the seed's;
 * What it taxes once no category reads Default or unanswered; Local rates
 * once the review report carries no location line for the state (a Location
 * whose address is only Estimated is one); and the
 * nexus warning once a threshold row stands and no crossing waits to be
 * accepted. A key the answer lacks reads as not done.
 */
export function stateSetupSteps(view: StateSetupView): Record<StateSetupStep, StateSetupStepFacts> {
  const registration = view.registration ?? null;
  const rate = view.rate ?? { inForce: false, seeded: false, label: null };
  const taxes = view.taxes ?? { categories: 0, unsettled: 1 };
  const local = view.local ?? { coverage: null, noCounty: 1, unconfirmed: 1 };
  const nexus = view.nexus ?? { thresholdSet: false, crossedOn: null, acknowledgedAt: null };
  const waiting = nexus.crossedOn !== null && nexus.acknowledgedAt === null;
  return {
    register: { done: registration !== null && Boolean(registration.accountNumber?.trim()), reachable: !view.noStatewideTax },
    frequency: { done: registration?.filingFrequency != null, reachable: registration !== null },
    rate: { done: rate.inForce && !rate.seeded, reachable: true },
    taxes: { done: taxes.unsettled === 0, reachable: true },
    local: { done: local.noCounty === 0 && local.unconfirmed === 0 && (local.estimated ?? 0) === 0, reachable: true },
    nexus: { done: nexus.thresholdSet && !waiting, reachable: true },
  };
}

/** How many of the six steps are left, which the state's facts band names beside Set up. */
export function stateSetupStepsLeft(view: StateSetupView): number {
  const steps = stateSetupSteps(view);
  return STATE_SETUP_STEPS.filter((step) => !steps[step].done).length;
}

/** The frequency step's write: the fields the registration route takes, each optional (decision 1780). */
export interface TaxRegistrationChange {
  accountNumber?: string | null;
  filingFrequency?: FilingFrequencyValue | null;
  dueDayOfMonth?: number | null;
  dueMonthOffset?: number | null;
}

/** The local step's list for one state (decision 1832); a browser fetch path, relative to the API root. */
export const stateLocalReviewPath = (stateCode: string): string => `/tax/states/${encodeURIComponent(stateCode)}/local-review`;

/** One Location's confirmation, written by the Location rather than a site (decision 1832). */
export const locationJurisdictionConfirmPath = (locationId: string): string => `/tax/locations/${encodeURIComponent(locationId)}/jurisdiction/confirm`;

/** Why a Location is on the review report, or null once it is settled (decision 0736). */
export type LocalReviewReason = 'county-not-entered' | 'jurisdiction-not-confirmed' | 'address-estimated';

/** 🔒 One Location the local step lists (decision 1832), read from its own row and stored nowhere else. */
export interface LocalReviewLocation {
  locationId: string;
  /** The building's name, or its first address line where it carries none. */
  label: string;
  /** The address as typed, on one line; null for a place named rather than addressed. */
  address: string | null;
  county: string | null;
  reason: LocalReviewReason | null;
  /** The day the looked-up jurisdiction was confirmed, a calendar day. */
  confirmedOn: string | null;
  /** A place named rather than addressed, which has nothing to verify (decision 0673). */
  unaddressed: boolean;
  /** The Location's stored verification status (decision 2040). */
  verificationStatus: VerificationStatus;
  /** True where the last verification failed (decision 2040). */
  verificationFailed: boolean;
}

/** 🔒 The local step's read (decision 1832): the coverage in force, the Locations the review report counts, and the untaxed invoices (decision 1830). */
export interface StateLocalReview {
  stateCode: string;
  coverage: LocalTaxCoverageValue | null;
  locations: LocalReviewLocation[];
  invoices: UnmappedTaxPairingRow[];
}
